import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const PerformanceContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const MetricCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 1.1rem;
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
    font-weight: 500;
  }
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PerformanceTest = () => {
  const [metrics, setMetrics] = useState({
    pageLoad: {
      loadTime: null,
      timeToInteractive: null,
      resourceLoadTime: null
    },
    runtime: {
      renderTime: null,
      memoryUsage: null,
      fps: null
    },
    network: {
      apiLatency: null,
      resourceSpeed: null,
      cacheHitRate: null
    },
    assets: {
      imageOptimization: null,
      bundleSize: null,
      codeSplitting: null
    }
  });

  const [isRunning, setIsRunning] = useState(false);

  const measurePageLoadMetrics = () => {
    const navigationTiming = window.performance.timing;
    const loadTime = navigationTiming.loadEventEnd - navigationTiming.navigationStart;
    const timeToInteractive = navigationTiming.domInteractive - navigationTiming.navigationStart;
    const resourceLoadTime = navigationTiming.loadEventEnd - navigationTiming.responseEnd;

    setMetrics(prev => ({
      ...prev,
      pageLoad: {
        loadTime: loadTime / 1000, // Convert to seconds
        timeToInteractive: timeToInteractive / 1000,
        resourceLoadTime: resourceLoadTime / 1000
      }
    }));
  };

  const measureRuntimeMetrics = () => {
    // Measure component render time
    const startTime = performance.now();
    // Simulate some rendering work
    setTimeout(() => {
      const renderTime = performance.now() - startTime;
      
      // Get memory usage if available
      const memory = performance.memory?.usedJSHeapSize / (1024 * 1024) || null;
      
      // Calculate FPS
      let fps = 60; // Default to 60fps
      if (window.requestAnimationFrame) {
        let frameCount = 0;
        let lastTime = performance.now();
        
        const countFrames = (timestamp) => {
          frameCount++;
          if (timestamp - lastTime > 1000) {
            fps = frameCount;
            frameCount = 0;
            lastTime = timestamp;
            
            setMetrics(prev => ({
              ...prev,
              runtime: {
                renderTime: renderTime.toFixed(2),
                memoryUsage: memory?.toFixed(2) || 'Not available',
                fps: fps
              }
            }));
          } else {
            requestAnimationFrame(countFrames);
          }
        };
        
        requestAnimationFrame(countFrames);
      }
    }, 100);
  };

  const measureNetworkMetrics = async () => {
    try {
      // Measure API latency
      const startTime = performance.now();
      await fetch('/api/health');
      const apiLatency = performance.now() - startTime;

      // Get resource timing data
      const resources = performance.getEntriesByType('resource');
      const avgResourceSpeed = resources.reduce((acc, resource) => 
        acc + resource.duration, 0) / resources.length;

      // Calculate cache hit rate
      const cachedResources = resources.filter(resource => 
        resource.transferSize === 0 || resource.transferSize < resource.decodedBodySize);
      const cacheHitRate = (cachedResources.length / resources.length) * 100;

      setMetrics(prev => ({
        ...prev,
        network: {
          apiLatency: apiLatency.toFixed(2),
          resourceSpeed: avgResourceSpeed.toFixed(2),
          cacheHitRate: cacheHitRate.toFixed(2)
        }
      }));
    } catch (error) {
      console.error('Network metrics measurement failed:', error);
    }
  };

  const measureAssetMetrics = () => {
    // Get all images on the page
    const images = document.getElementsByTagName('img');
    let totalOptimization = 0;

    for (let img of images) {
      const actualSize = img.naturalWidth * img.naturalHeight;
      const displaySize = img.width * img.height;
      totalOptimization += displaySize / actualSize;
    }

    const avgImageOptimization = (totalOptimization / images.length * 100) || 0;

    // Get bundle information from window if available
    const bundleSize = window.__BUNDLE_SIZE__ || 'Not available';
    const codeSplitting = window.__CHUNKS_COUNT__ || 'Not available';

    setMetrics(prev => ({
      ...prev,
      assets: {
        imageOptimization: avgImageOptimization.toFixed(2),
        bundleSize,
        codeSplitting
      }
    }));
  };

  const runAllTests = async () => {
    setIsRunning(true);
    measurePageLoadMetrics();
    measureRuntimeMetrics();
    await measureNetworkMetrics();
    measureAssetMetrics();
    setIsRunning(false);
  };

  const getMetricStatus = (category, metric, value) => {
    const thresholds = {
      pageLoad: {
        loadTime: { warning: 3, error: 5 },
        timeToInteractive: { warning: 2, error: 4 },
        resourceLoadTime: { warning: 2, error: 4 }
      },
      runtime: {
        renderTime: { warning: 100, error: 200 },
        memoryUsage: { warning: 50, error: 100 },
        fps: { warning: 45, error: 30 }
      },
      network: {
        apiLatency: { warning: 300, error: 1000 },
        resourceSpeed: { warning: 1000, error: 3000 },
        cacheHitRate: { warning: 50, error: 20 }
      }
    };

    if (!thresholds[category] || !thresholds[category][metric]) return 'good';
    
    const { warning, error } = thresholds[category][metric];
    if (value > error) return 'error';
    if (value > warning) return 'warning';
    return 'good';
  };

  return (
    <PerformanceContainer>
      <TestSection>
        <h3>Performance Metrics</h3>
        <ActionButton onClick={runAllTests} disabled={isRunning}>
          {isRunning ? 'Running Tests...' : 'Run All Tests'}
        </ActionButton>

        <TestResults>
          <h3>Page Load Performance</h3>
          <MetricGrid>
            <MetricCard status={getMetricStatus('pageLoad', 'loadTime', metrics.pageLoad.loadTime)}>
              <h4>Total Load Time</h4>
              <p>{metrics.pageLoad.loadTime ? `${metrics.pageLoad.loadTime}s` : 'Not measured'}</p>
            </MetricCard>
            <MetricCard status={getMetricStatus('pageLoad', 'timeToInteractive', metrics.pageLoad.timeToInteractive)}>
              <h4>Time to Interactive</h4>
              <p>{metrics.pageLoad.timeToInteractive ? `${metrics.pageLoad.timeToInteractive}s` : 'Not measured'}</p>
            </MetricCard>
            <MetricCard status={getMetricStatus('pageLoad', 'resourceLoadTime', metrics.pageLoad.resourceLoadTime)}>
              <h4>Resource Load Time</h4>
              <p>{metrics.pageLoad.resourceLoadTime ? `${metrics.pageLoad.resourceLoadTime}s` : 'Not measured'}</p>
            </MetricCard>
          </MetricGrid>

          <h3>Runtime Performance</h3>
          <MetricGrid>
            <MetricCard status={getMetricStatus('runtime', 'renderTime', metrics.runtime.renderTime)}>
              <h4>Component Render Time</h4>
              <p>{metrics.runtime.renderTime ? `${metrics.runtime.renderTime}ms` : 'Not measured'}</p>
            </MetricCard>
            <MetricCard status={getMetricStatus('runtime', 'memoryUsage', metrics.runtime.memoryUsage)}>
              <h4>Memory Usage</h4>
              <p>{metrics.runtime.memoryUsage ? `${metrics.runtime.memoryUsage}MB` : 'Not measured'}</p>
            </MetricCard>
            <MetricCard status={getMetricStatus('runtime', 'fps', metrics.runtime.fps)}>
              <h4>Frames Per Second</h4>
              <p>{metrics.runtime.fps ? `${metrics.runtime.fps}fps` : 'Not measured'}</p>
            </MetricCard>
          </MetricGrid>

          <h3>Network Performance</h3>
          <MetricGrid>
            <MetricCard status={getMetricStatus('network', 'apiLatency', metrics.network.apiLatency)}>
              <h4>API Latency</h4>
              <p>{metrics.network.apiLatency ? `${metrics.network.apiLatency}ms` : 'Not measured'}</p>
            </MetricCard>
            <MetricCard status={getMetricStatus('network', 'resourceSpeed', metrics.network.resourceSpeed)}>
              <h4>Resource Download Speed</h4>
              <p>{metrics.network.resourceSpeed ? `${metrics.network.resourceSpeed}ms` : 'Not measured'}</p>
            </MetricCard>
            <MetricCard status={getMetricStatus('network', 'cacheHitRate', metrics.network.cacheHitRate)}>
              <h4>Cache Hit Rate</h4>
              <p>{metrics.network.cacheHitRate ? `${metrics.network.cacheHitRate}%` : 'Not measured'}</p>
            </MetricCard>
          </MetricGrid>

          <h3>Asset Optimization</h3>
          <MetricGrid>
            <MetricCard status="good">
              <h4>Image Optimization</h4>
              <p>{metrics.assets.imageOptimization ? `${metrics.assets.imageOptimization}%` : 'Not measured'}</p>
            </MetricCard>
            <MetricCard status="good">
              <h4>Bundle Size</h4>
              <p>{metrics.assets.bundleSize || 'Not measured'}</p>
            </MetricCard>
            <MetricCard status="good">
              <h4>Code Splitting</h4>
              <p>{metrics.assets.codeSplitting || 'Not measured'}</p>
            </MetricCard>
          </MetricGrid>
        </TestResults>
      </TestSection>
    </PerformanceContainer>
  );
};

export default PerformanceTest; 